import React, { useEffect, useRef, useState } from 'react';
import logo from '../../assets/wigley-logo.png';
import Nav from './Nav';

// Short two-note chime via Web Audio (no asset to load). One lazily-created, reused AudioContext (browsers cap how many
// you can open, and per-ding allocation is wasteful). Fully guarded: before the user has interacted with the page the
// context stays 'suspended' and this is silently a no-op — exactly the graceful autoplay degradation we want.
let _audioCtx = null;
function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const t0 = ctx.currentTime;
    [[880, 0], [1174.66, 0.10]].forEach(([freq, at]) => {   // A5 → D6, quick and soft
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + at);
      gain.gain.exponentialRampToValueAtTime(0.14, t0 + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.20);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t0 + at); osc.stop(t0 + at + 0.22);
    });
  } catch { /* no Web Audio / autoplay blocked — stay silent */ }
}

// Notification bell — global (every page, incl. the dashboard). Logic (query/mark-read/deep-link) lives in
// StaffPortal; this just renders the unread list and reports clicks up.
function NotificationBell({ notifications, onNotificationClick, onMarkAllRead }) {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem('wh_notif_muted') === '1'; } catch { return false; } });
  const prevCount = useRef(null);   // null = first render; used to ring ONLY when the unread count rises
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', onDocClick); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const list = notifications || [];
  const count = list.length;
  // Ring when the unread count RISES (new notification arrived) — not on mark-read, and not for the unread that were
  // already there on first load. muted from this render is captured for this count value (correct: both come from the
  // same render). exhaustive-deps intentionally omits muted (project-wide rule); keying on count is the trigger.
  useEffect(() => {
    const prev = prevCount.current;
    prevCount.current = count;
    if (prev === null) return;              // first render — establish baseline, don't ring
    if (count > prev && !muted) playChime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button onClick={() => setOpen(o => !o)} aria-label="Notifications" title="Notifications"
        style={{ position: 'relative', width: '38px', height: '38px', borderRadius: '50%', background: 'transparent', border: '1px solid var(--line)', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        🔔
        {count > 0 && <span style={{ position: 'absolute', top: '-4px', right: '-4px', minWidth: '17px', height: '17px', padding: '0 4px', borderRadius: '9px', background: 'var(--red)', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{count > 9 ? '9+' : count}</span>}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: '320px', background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', zIndex: 100, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--charcoal)' }}>Notifications</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {count > 0 && <button onClick={() => onMarkAllRead && onMarkAllRead()} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>Mark all read</button>}
              <button onClick={() => setMuted(m => { const nv = !m; try { localStorage.setItem('wh_notif_muted', nv ? '1' : '0'); } catch {} return nv; })}
                title={muted ? 'Notification sound off — click to turn on' : 'Notification sound on — click to mute'}
                aria-label={muted ? 'Turn notification sound on' : 'Mute notification sound'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', lineHeight: 1 }}>{muted ? '🔕' : '🔊'}</button>
            </div>
          </div>
          <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
            {count === 0 ? (
              <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--slate)', fontSize: '12px' }}>You're all caught up.</div>
            ) : list.map(n => (
              <button key={n.id} onClick={() => { setOpen(false); onNotificationClick && onNotificationClick(n); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--line)', cursor: 'pointer', fontFamily: 'inherit' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--red-tint)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ fontSize: '12px', color: 'var(--ink)', marginBottom: '2px' }}>{n.body || 'Notification'}</div>
                <div style={{ fontSize: '10px', color: 'var(--slate)' }}>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Unified app header (Phase 1).
// Sticky white strip; logo + nav on the left, user identity + avatar on
// the right. Avatar opens a small menu containing Sign Out so the action
// stays accessible without cluttering the bar.
export default function Header({ user, staffUser, activePage, onActivePageChange, onSignOut, notifications, onNotificationClick, onMarkAllRead }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const displayName = staffUser?.name || user?.email || '';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .map(s => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: 'var(--panel)',
      borderBottom: '1px solid var(--line)',
      height: '72px',
      padding: '0 26px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '24px',
      boxShadow: 'var(--shadow-soft)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '32px', minWidth: 0 }}>
        <img src={logo} alt="Wigley" style={{ height: '64px', width: 'auto', display: 'block', flexShrink: 0 }} />
        <Nav activePage={activePage} onActivePageChange={onActivePageChange} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <NotificationBell notifications={notifications} onNotificationClick={onNotificationClick} onMarkAllRead={onMarkAllRead} />
        <div ref={menuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--charcoal)' }}>{displayName}</div>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em', color: 'var(--red)', textTransform: 'uppercase' }}>
            Operations
          </div>
        </div>
        <button
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Account menu"
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: 'var(--red)',
            color: 'white',
            border: 'none',
            fontSize: '13px',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'inherit',
          }}>
          {initials}
        </button>
        {menuOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            background: 'var(--panel)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow)',
            minWidth: '160px',
            padding: '6px',
            zIndex: 100,
          }}>
            <button
              onClick={() => { setMenuOpen(false); onSignOut && onSignOut(); }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '9px 12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: 'var(--ink)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--red-tint)'; e.currentTarget.style.color = 'var(--red-dark)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink)'; }}>
              Sign Out
            </button>
          </div>
        )}
        </div>
      </div>
    </header>
  );
}
